import {Footer, Navbar} from '../components';
import {AnimatePresence, motion, useInView, useMotionValue, useSpring} from 'framer-motion';
import {DASHBOARD_URI} from '../lib/constants';
import React, {ReactElement, useEffect, useRef, useState} from 'react';
import useInterval from '../lib/interval';
import {useStats} from '../lib/hooks/stats';
import Artur from '../../public/assets/artur.png';
import Joe from '../../public/assets/joe.png';
import Noah from '../../public/assets/noah.png';
import Pierre from '../../public/assets/pierre.png';
import Jonni from '../../public/assets/jonni.png';
import Alisson from '../../public/assets/alisson.png';
import Image from 'next/image';
import Script from 'next/script';
import dayjs from 'dayjs';
import Link from 'next/link';
import Prism from 'prismjs';
import {Editor} from '../components/MarkdownEditor';
import generateRssFeed from '../lib/rss-feed';
import Flow from '../../public/assets/flow.svg';
import {Leaf, PackageOpen} from 'lucide-react';

/**
 *
 * @param root0
 * @param root0.value
 */
function AnimatedNumber({value}: {value: number}) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue);
  const isInView = useInView(ref, {once: true});

  useEffect(() => {
    if (isInView) {
      motionValue.set(value);
    }
  }, [motionValue, value, isInView]);

  useEffect(
    () =>
      springValue.onChange(latest => {
        if (ref.current) {
          // Format with K if over 100.000
          if (latest > 100000) {
            ref.current.textContent = Intl.NumberFormat('en-US', {notation: 'compact'}).format(latest);
            return;
          }
          ref.current.textContent = Intl.NumberFormat('en-US').format(parseFloat(latest.toFixed(0)));
        }
      }),
    [springValue],
  );

  return <span ref={ref} />;
}

const cardsToRender: {
  icon: ReactElement;
  title: string;
  delay?: number;
}[] = [
  {
    title: 'Triggered account-created',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <path d="M8 9l3 3l-3 3"></path>
        <line x1="13" y1="15" x2="16" y2="15"></line>
        <rect x="3" y="4" width="18" height="16" rx="2"></rect>
      </svg>
    ),
    delay: 0,
  },
  {
    title: 'Started onboarding flow',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <path d="M16 21h3c.81 0 1.48 -.67 1.48 -1.48l.02 -.02c0 -.82 -.69 -1.5 -1.5 -1.5h-3v3z"></path>
        <path d="M16 15h2.5c.84 -.01 1.5 .66 1.5 1.5s-.66 1.5 -1.5 1.5h-2.5v-3z"></path>
        <path d="M4 9v-4c0 -1.036 .895 -2 2 -2s2 .964 2 2v4"></path>
        <path d="M2.99 11.98a9 9 0 0 0 9 9m9 -9a9 9 0 0 0 -9 -9"></path>
        <path d="M8 7h-4"></path>
      </svg>
    ),
    delay: 1000,
  },
  {
    title: 'Welcome email delivered',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <rect x="3" y="5" width="18" height="14" rx="2"></rect>
        <polyline points="3 7 12 13 21 7"></polyline>
      </svg>
    ),
    delay: 750,
  },
  {
    title: 'Starting feedback flow in 2 days',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <path d="M16 21h3c.81 0 1.48 -.67 1.48 -1.48l.02 -.02c0 -.82 -.69 -1.5 -1.5 -1.5h-3v3z"></path>
        <path d="M16 15h2.5c.84 -.01 1.5 .66 1.5 1.5s-.66 1.5 -1.5 1.5h-2.5v-3z"></path>
        <path d="M4 9v-4c0 -1.036 .895 -2 2 -2s2 .964 2 2v4"></path>
        <path d="M2.99 11.98a9 9 0 0 0 9 9m9 -9a9 9 0 0 0 -9 -9"></path>
        <path d="M8 7h-4"></path>
      </svg>
    ),
    delay: 2000,
  },
  {
    title: 'Welcome email opened',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <polyline points="3 9 12 15 21 9 12 3 3 9"></polyline>
        <path d="M21 9v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10"></path>
        <line x1="3" y1="19" x2="9" y2="13"></line>
        <line x1="15" y1="13" x2="21" y2="19"></line>
      </svg>
    ),
    delay: 4000,
  },
  {
    title: 'Clicked on useplunk.com',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <line x1="3" y1="12" x2="6" y2="12"></line>
        <line x1="12" y1="3" x2="12" y2="6"></line>
        <line x1="7.8" y1="7.8" x2="5.6" y2="5.6"></line>
        <line x1="16.2" y1="7.8" x2="18.4" y2="5.6"></line>
        <line x1="7.8" y1="16.2" x2="5.6" y2="18.4"></line>
        <path d="M12 12l9 3l-4 2l-2 4l-3 -9"></path>
      </svg>
    ),
    delay: 3000,
  },
  {
    title: 'Triggered upgraded-plan',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <path d="M8 9l3 3l-3 3"></path>
        <line x1="13" y1="15" x2="16" y2="15"></line>
        <rect x="3" y="4" width="18" height="16" rx="2"></rect>
      </svg>
    ),
    delay: 4000,
  },
  {
    title: 'Started upgrade flow',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <path d="M16 21h3c.81 0 1.48 -.67 1.48 -1.48l.02 -.02c0 -.82 -.69 -1.5 -1.5 -1.5h-3v3z"></path>
        <path d="M16 15h2.5c.84 -.01 1.5 .66 1.5 1.5s-.66 1.5 -1.5 1.5h-2.5v-3z"></path>
        <path d="M4 9v-4c0 -1.036 .895 -2 2 -2s2 .964 2 2v4"></path>
        <path d="M2.99 11.98a9 9 0 0 0 9 9m9 -9a9 9 0 0 0 -9 -9"></path>
        <path d="M8 7h-4"></path>
      </svg>
    ),
    delay: 500,
  },
  {
    title: 'Thank you email delivered',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        fill={'none'}
      >
        <rect x="3" y="5" width="18" height="14" rx="2"></rect>
        <polyline points="3 7 12 13 21 7"></polyline>
      </svg>
    ),
    delay: 750,
  },
];

const testimonials = [
  {
    testimonial: 'Transparent and intuitive UI, extremely easy setup & automation and great support.',
    author: 'Artur Czemiel',
    image: <Image placeholder={'blur'} src={Artur} alt={'Artur Czemiel'} />,
    role: (
      <span>
        Founder at{' '}
        <a href={'https://graphqleditor.com'} target={'_blank'}>
          GraphQL Editor
        </a>
      </span>
    ),
  },
  {
    testimonial: 'Lots of care put into Plunk',
    author: 'Jonni Lundy',
    image: <Image src={Jonni} alt={'Jonni Lundy'} />,
    role: (
      <span>
        Founding Operations Manager at{' '}
        <a href={'https://www.resend.com'} target={'_blank'}>
          Resend
        </a>
      </span>
    ),
  },
  {
    testimonial:
      'I’ve been using Plunk for building & sending out marketing emails and genuinely love it! Highly recommend!',
    author: 'Joe Ashwell',
    image: <Image src={Joe} alt={'Joe Ashwell'} />,
    role: (
      <span>
        Founder at{' '}
        <a href={'https://www.unwindhr.com'} target={'_blank'}>
          UnwindHR
        </a>
      </span>
    ),
  },
  {
    testimonial: 'I loved the ease of use, beautiful UI and great UX. Everything simply works.',
    author: 'Alisson Leal',
    image: <Image src={Alisson} alt={'Alisson Leal'} />,
    role: (
      <span>
        Founder at{' '}
        <a href={'https://brapi.dev'} target={'_blank'}>
          Brapi
        </a>
      </span>
    ),
  },
  {
    testimonial: 'Simple to use, efficient and no regrets!',
    author: 'Noah Di Gesu',
    image: <Image src={Noah} alt={'Noah Di Gesu'} />,
    role: (
      <span>
        Founder at{' '}
        <a href={'https://smoothey.app'} target={'_blank'}>
          Smoothey
        </a>
      </span>
    ),
  },
  {
    testimonial: 'Clean design, easy to understand, fair pricing.',
    author: 'Pierre Jacquel',
    image: <Image src={Pierre} alt={'Pierre Jacquel'} />,
    role: (
      <span>
        Founder at{' '}
        <a href={'https://landingly.co'} target={'_blank'}>
          Landingly
        </a>
      </span>
    ),
  },
];

/**
 *
 */
export default function Index() {
  const {data: stats} = useStats();

  const [cards, setCards] = useState<
    {
      icon: ReactElement;
      title: string;
    }[]
  >([cardsToRender[0]]);
  const [testimonial, setTestimonial] = useState(0);
  const [tool, setTool] = useState(0);
  const [language, setLanguage] = useState(2);
  const [template, setTemplate] = useState('');

  useEffect(() => {
    const highlight = async () => {
      await Prism.highlightAll(); // <--- prepare Prism
    };
    void highlight(); // <--- call the async function
  }, [language]);

  useInterval(() => {
    if (cards.length === cardsToRender.length) {
      return;
    }

    setCards(cards => [cardsToRender[cards.length], ...cards]);
  }, cardsToRender[cards.length]?.delay ?? 1000);

  useInterval(() => {
    setTestimonial((testimonial + 1) % testimonials.length);
  }, 5500);

  useInterval(() => {
    setTool((tool + 1) % 3);
  }, 5500);

  return (
    <>
      <Script
        id={`faq-schema-index`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': [
              {
                '@type': 'Question',
                'name': 'How is Plunk different from other email automation tools?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text':
                    'Plunk is the email tool built for SaaS businesses, indie hackers and developers. Plunk allows you to create complex, automated email flows and to trigger them from anywhere through a single API call.',
                },
              },
              {
                '@type': 'Question',
                'name': 'Can I use Plunk for transactional emails?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text':
                    'Yes, Plunk is a full-fledged email tool that allows you to send transactional emails as well as marketing emails. You can use Plunk to send emails to your customers when they sign up, when they cancel their subscription, when they upgrade their plan, etc.',
                },
              },
              {
                '@type': 'Question',
                'name': 'Can I use Plunk for sending newsletters?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text':
                    'Yes, Plunk allows you to send newsletters and other broadcast emails to multiple contacts at once.',
                },
              },
              {
                '@type': 'Question',
                'name': 'What programming languages and frameworks does Plunk support?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text':
                    'Plunk supports all programming languages and frameworks that are capable of sending API calls.. You can use Plunk to send emails from your backend, frontend, mobile app, or any other platform.',
                },
              },
              {
                '@type': 'Question',
                'name': 'How much does Plunk cost?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text':
                    'Plunk offers a free plan that includes 3000 automated, 3000 transactional and 3000 broadcast emails per month. You can upgrade to a pay-as-you-go plan that costs $0.005 per email.',
                },
              },
            ],
          }),
        }}
      />
      <Script
        id={`corp-schema-index`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Corporation',
            'name': 'Plunk',
            'alternateName': 'UsePlunk',
            'url': 'https://www.useplunk.com',
            'logo': 'https://www.useplunk.com/assets/logo.png',
            'sameAs': ['https://www.twitter.com/useplunk', 'https://www.useplunk.com'],
          }),
        }}
      />

      <Navbar />

      <main className={'mx-auto max-w-7xl space-y-40 px-8 sm:px-0'}>
        <section className={'mt-24 space-y-16'}>
          <div className={'flex flex-col items-center gap-y-6 text-center'}>
            <h1 className={'max-w-lg text-5xl font-bold text-neutral-800 sm:text-6xl'}>
              <span className={'text-balance leading-tight tracking-tight'}>
                The <span className={'whitespace-nowrap'}>Open-Source</span> Email Platform
              </span>
            </h1>
            <p className={'max-w-sm text-balance sm:max-w-xl'}>
              Plunk is the open-source, affordable email platform that brings together marketing, transactional and
              broadcast emails into one single, complete solution
            </p>

            <motion.a
              whileHover={{scale: 1.1}}
              whileTap={{scale: 0.9}}
              href={`${DASHBOARD_URI}/auth/signup`}
              className={'h-full rounded-lg bg-neutral-800 px-12 py-4 font-semibold text-white'}
            >
              Start sending for free
            </motion.a>

            <div className={'flex flex-col items-center gap-1'}>
              <Link href={'https://www.websitecarbon.com/website/useplunk.com/'} target={'_blank'}>
                <div className={'inline-flex gap-3 rounded border border-neutral-100'}>
                  <div className={'flex items-center gap-3 px-6 py-3'}>
                    <Leaf size={16} />
                    <span className={'text-sm'}>0.15/g CO2 per visit</span>
                  </div>
                  <div className={'flex items-center justify-center rounded-r bg-green-100 p-3 text-sm font-medium'}>
                    A
                  </div>
                </div>
              </Link>
              <span className={'text-xs text-neutral-500'}>Cleaner than 85% of the web</span>
            </div>
          </div>

          <div className={'mx-auto max-w-3xl'}>
            <motion.div
              layout
              className={
                'scrollbar-thin scrollbar-thumb-rounded-xl scrollbar-track-rounded scrollbar-thumb-neutral-200 scrollbar-track-neutral-100 h-[400px] space-y-3 overflow-y-scroll rounded-bl-xl rounded-br-md rounded-tl-xl rounded-tr-md bg-blue-50 p-6'
              }
            >
              <AnimatePresence>
                {cards.map(c => {
                  return (
                    <motion.div
                      layout
                      key={c.title}
                      initial={{opacity: 0, y: 100}}
                      animate={{opacity: 1, y: 0}}
                      exit={{opacity: 0, y: -100}}
                      className={
                        'mx-auto flex w-full max-w-sm items-center gap-x-4 rounded-md border border-neutral-100 bg-white px-8 py-3 text-sm font-semibold text-neutral-800'
                      }
                    >
                      <span className={'rounded-md bg-blue-50 p-2'}>{c.icon}</span>

                      <div>
                        <span className={'block'}>{c.title}</span>
                        <span className={'block text-xs font-normal text-neutral-400'}>hello@useplunk.com</span>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>

            <div className={'mt-3 h-10'}>
              <AnimatePresence>
                {cards.length === cardsToRender.length && (
                  <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className={'w-full'}>
                    <svg
                      onClick={() => setCards([])}
                      className={
                        'ml-auto h-8 w-8 cursor-pointer text-neutral-400 transition ease-in-out hover:rotate-180 hover:text-neutral-700'
                      }
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                    >
                      <path d="M11.25 4.75L8.75 7L11.25 9.25"></path>
                      <path d="M12.75 19.25L15.25 17L12.75 14.75"></path>
                      <path d="M9.75 7H13.25C16.5637 7 19.25 9.68629 19.25 13V13.25"></path>
                      <path d="M14.25 17H10.75C7.43629 17 4.75 14.3137 4.75 11V10.75"></path>
                    </svg>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </section>

        <section className={'space-y-16'}>
          <div className={'w-full space-y-3 text-center'}>
            <h2 className={'text-4xl font-bold text-neutral-800'}>Stop using different tools</h2>
            <p className={'mx-auto max-w-xl text-balance'}>
              Different APIs, different UIs, different pricing. It's time to stop using different tools for your emails.
            </p>
          </div>

          <div className={'mx-auto max-w-4xl'}>
            <Flow />
          </div>
        </section>

        <section className={'space-y-16'}>
          <div className={'w-full space-y-3 text-center'}>
            <h2 className={'text-4xl font-bold text-neutral-800'}>Get everything in one</h2>
            <p className={'mx-auto max-w-xl'}>Tackle all your email problems with one single solution</p>
          </div>

          <div className={'mx-auto grid max-w-4xl gap-6 sm:grid-cols-8'}>
            <div className={'flex flex-col justify-center gap-9 sm:col-span-3 sm:items-end'}>
              <div className={'flex cursor-pointer gap-x-3'} onClick={() => setTool(0)}>
                <div className={'order-2 text-left sm:order-1 sm:text-right'}>
                  <p className={'text-xl font-medium'}>Actions</p>
                  <p>Powerful, repeatable automations</p>
                </div>
                <div
                  className={`${
                    tool === 0 ? 'bg-blue-50' : 'bg-neutral-200'
                  } duration-250 order-1 min-h-full w-1.5 rounded-full transition sm:order-2`}
                ></div>
              </div>
              <div className={'flex cursor-pointer gap-x-3'} onClick={() => setTool(1)}>
                <div className={'order-2 text-left sm:order-1 sm:text-right'}>
                  <p className={'text-xl font-medium'}>Transactional</p>
                  <p>Critical emails that need to arrive fast</p>
                </div>
                <div
                  className={`${
                    tool === 1 ? 'bg-blue-50' : 'bg-neutral-200'
                  } duration-250 order-1 min-h-full w-1.5 rounded-full transition sm:order-2`}
                ></div>
              </div>
              <div className={'flex cursor-pointer gap-x-3'} onClick={() => setTool(2)}>
                <div className={'order-2 text-left sm:order-1 sm:text-right'}>
                  <p className={'text-xl font-medium'}>Campaigns</p>
                  <p>Newsletters and broadcasts</p>
                </div>
                <div
                  className={`${
                    tool === 2 ? 'bg-blue-50' : 'bg-neutral-200'
                  } duration-250 order-1 min-h-full w-1.5 rounded-full transition sm:order-2`}
                ></div>
              </div>
            </div>
            <div className={'h-80 rounded-xl bg-blue-50 p-6 sm:col-span-5'}>
              <AnimatePresence mode={'wait'}>
                {tool === 0 ? (
                  <motion.div
                    key={'actions'}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    className={'flex h-full w-full flex-col items-center justify-center gap-3'}
                  >
                    <div
                      className={
                        'mx-auto flex w-full max-w-xs items-center gap-x-4 rounded-md border border-neutral-100 bg-white px-8 py-3 text-sm font-semibold text-neutral-800 sm:max-w-sm'
                      }
                    >
                      <span className={'rounded-md bg-blue-50 p-2'}>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          fill={'none'}
                        >
                          <path d="M8 9l3 3l-3 3"></path>
                          <line x1="13" y1="15" x2="16" y2="15"></line>
                          <rect x="3" y="4" width="18" height="16" rx="2"></rect>
                        </svg>
                      </span>

                      <div>
                        <span className={'block'}>Account created</span>
                      </div>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="89" viewBox="0 0 42 89" fill="none">
                      <path
                        d="M11.1821 36.3171L10.808 36.9672L10.808 36.9672L11.1821 36.3171ZM35.7203 50.435L36.0944 49.7849L36.0944 49.7849L35.7203 50.435ZM37.0353 66.4711L36.5604 65.8906L36.5604 65.8906L37.0353 66.4711ZM9.5 89L18.0467 87.602L12.5627 80.8994L9.5 89ZM14.7425 2.70969C15.1344 2.57573 15.3436 2.1494 15.2096 1.75744C15.0757 1.36549 14.6493 1.15634 14.2574 1.2903L14.7425 2.70969ZM9.2383 3.81799C8.89846 4.05481 8.81495 4.52228 9.05176 4.86212C9.28858 5.20196 9.75606 5.28547 10.0959 5.04865L9.2383 3.81799ZM3.74311 13.1072C3.89546 12.722 3.70673 12.2862 3.32155 12.1339C2.93637 11.9815 2.50062 12.1703 2.34826 12.5554L3.74311 13.1072ZM1.27762 23.6292C1.35337 24.0364 1.74489 24.3052 2.15212 24.2294C2.55935 24.1537 2.82808 23.7622 2.75233 23.3549L1.27762 23.6292ZM7.44365 32.4812C7.15548 32.1837 6.68066 32.1761 6.38313 32.4643C6.08559 32.7524 6.078 33.2272 6.36618 33.5248L7.44365 32.4812ZM16.9426 40.4966C17.3016 40.7032 17.7601 40.5796 17.9667 40.2206C18.1733 39.8615 18.0497 39.403 17.6907 39.1965L16.9426 40.4966ZM29.9598 46.2554C29.6008 46.0489 29.1422 46.1725 28.9357 46.5315C28.7291 46.8905 28.8527 47.349 29.2117 47.5556L29.9598 46.2554ZM38.4734 54.0677C38.6949 54.4177 39.1582 54.5219 39.5083 54.3004C39.8583 54.0789 39.9625 53.6156 39.741 53.2656L38.4734 54.0677ZM40.5411 63.0233C40.7026 62.6419 40.5244 62.2018 40.1429 62.0403C39.7615 61.8787 39.3214 62.057 39.1599 62.4384L40.5411 63.0233ZM33.1185 68.7067C32.7979 68.969 32.7506 69.4416 33.0129 69.7621C33.2752 70.0827 33.7477 70.13 34.0683 69.8677L33.1185 68.7067ZM27.1845 75.4999C27.5051 75.2376 27.5523 74.7651 27.29 74.4445C27.0277 74.1239 26.5552 74.0767 26.2346 74.339L27.1845 75.4999ZM19.3508 79.9712C19.0302 80.2335 18.983 80.706 19.2453 81.0266C19.5076 81.3472 19.9801 81.3944 20.3007 81.1321L19.3508 79.9712ZM13.4168 86.7644C13.7374 86.5021 13.7847 86.0295 13.5224 85.709C13.2601 85.3884 12.7876 85.3411 12.467 85.6034L13.4168 86.7644ZM14.2574 1.2903C12.3979 1.9258 10.7228 2.78353 9.2383 3.81799L10.0959 5.04865C11.4643 4.09503 13.014 3.30045 14.7425 2.70969L14.2574 1.2903ZM2.34826 12.5554C0.962107 16.0599 0.588498 19.9241 1.27762 23.6292L2.75233 23.3549C2.1148 19.9272 2.46072 16.3492 3.74311 13.1072L2.34826 12.5554ZM6.36618 33.5248C7.62497 34.8245 9.1048 35.9872 10.808 36.9672L11.5561 35.667C9.97275 34.756 8.60407 33.6793 7.44365 32.4812L6.36618 33.5248ZM10.808 36.9672L16.9426 40.4966L17.6907 39.1965L11.5561 35.667L10.808 36.9672ZM29.2117 47.5556L35.3463 51.0851L36.0944 49.7849L29.9598 46.2554L29.2117 47.5556ZM35.3463 51.0851C36.6825 51.8538 37.7265 52.8873 38.4734 54.0677L39.741 53.2656C38.8662 51.8832 37.6453 50.6772 36.0944 49.7849L35.3463 51.0851ZM39.1599 62.4384C38.6152 63.7247 37.7535 64.9145 36.5604 65.8906L37.5102 67.0516C38.8951 65.9185 39.9032 64.5298 40.5411 63.0233L39.1599 62.4384ZM36.5604 65.8906L33.1185 68.7067L34.0683 69.8677L37.5102 67.0516L36.5604 65.8906ZM26.2346 74.339L19.3508 79.9712L20.3007 81.1321L27.1845 75.4999L26.2346 74.339ZM11.1821 36.3171L10.434 37.6172L10.434 37.6172L11.1821 36.3171ZM35.7203 50.435L36.4684 49.1348L36.4684 49.1348L35.7203 50.435ZM37.0353 66.4711L36.0855 65.3102L36.0855 65.3102L37.0353 66.4711ZM9.5 89L26.5934 86.2041L15.6254 72.7988L9.5 89ZM14.985 3.41939C15.7689 3.15147 16.1872 2.2988 15.9193 1.51489C15.6514 0.730982 14.7987 0.31269 14.0148 0.580608L14.985 3.41939ZM8.8095 3.20266C8.12982 3.6763 7.9628 4.61124 8.43643 5.29092C8.91007 5.97059 9.84502 6.13762 10.5247 5.66398L8.8095 3.20266ZM4.44053 13.383C4.74524 12.6127 4.36776 11.7412 3.59741 11.4365C2.82706 11.1317 1.95555 11.5092 1.65084 12.2796L4.44053 13.383ZM0.540269 23.7664C0.691755 24.5808 1.47481 25.1183 2.28927 24.9668C3.10373 24.8153 3.64117 24.0322 3.48969 23.2178L0.540269 23.7664ZM7.98239 31.9594C7.40604 31.3643 6.45641 31.3492 5.86134 31.9255C5.26627 32.5019 5.25109 33.4515 5.82744 34.0466L7.98239 31.9594ZM16.5686 41.1467C17.2867 41.5599 18.2037 41.3127 18.6168 40.5946C19.0299 39.8765 18.7827 38.9595 18.0647 38.5464L16.5686 41.1467ZM30.3338 45.6053C29.6157 45.1922 28.6987 45.4394 28.2856 46.1575C27.8725 46.8755 28.1197 47.7925 28.8377 48.2057L30.3338 45.6053ZM37.8397 54.4687C38.2827 55.1688 39.2093 55.3771 39.9093 54.9341C40.6093 54.4911 40.8177 53.5645 40.3747 52.8645L37.8397 54.4687ZM41.2317 63.3158C41.5548 62.5529 41.1982 61.6727 40.4354 61.3496C39.6725 61.0266 38.7923 61.3831 38.4692 62.146L41.2317 63.3158ZM32.6435 68.1263C32.0024 68.6509 31.9079 69.5959 32.4325 70.2371C32.9571 70.8782 33.9021 70.9727 34.5433 70.4481L32.6435 68.1263ZM27.6594 76.0804C28.3006 75.5558 28.3951 74.6107 27.8705 73.9696C27.3459 73.3284 26.4009 73.2339 25.7597 73.7585L27.6594 76.0804ZM18.8759 79.3907C18.2347 79.9153 18.1402 80.8604 18.6648 81.5015C19.1894 82.1427 20.1344 82.2372 20.7756 81.7126L18.8759 79.3907ZM13.8918 87.3448C14.5329 86.8202 14.6274 85.8752 14.1028 85.234C13.5783 84.5929 12.6332 84.4984 11.9921 85.023L13.8918 87.3448ZM14.0148 0.580608C12.0899 1.23848 10.352 2.12778 8.8095 3.20266L10.5247 5.66398C11.8351 4.75079 13.322 3.98778 14.985 3.41939L14.0148 0.580608ZM1.65084 12.2796C0.212798 15.9152 -0.174651 19.9226 0.540269 23.7664L3.48969 23.2178C2.87795 19.9288 3.21003 16.4939 4.44053 13.383L1.65084 12.2796ZM5.82744 34.0466C7.13541 35.397 8.67083 36.6028 10.434 37.6172L11.9301 35.0169C10.4067 34.1405 9.09362 33.1067 7.98239 31.9594L5.82744 34.0466ZM10.434 37.6172L16.5686 41.1467L18.0647 38.5464L11.9301 35.0169L10.434 37.6172ZM28.8377 48.2057L34.9723 51.7352L36.4684 49.1348L30.3338 45.6053L28.8377 48.2057ZM34.9723 51.7352C36.2011 52.4421 37.1566 53.3893 37.8397 54.4687L40.3747 52.8645C39.436 51.3811 38.1267 50.0889 36.4684 49.1348L34.9723 51.7352ZM38.4692 62.146C37.9711 63.3222 37.1826 64.4125 36.0855 65.3102L37.9852 67.632C39.4659 66.4205 40.5472 64.9323 41.2317 63.3158L38.4692 62.146ZM36.0855 65.3102L32.6435 68.1263L34.5433 70.4481L37.9852 67.632L36.0855 65.3102ZM25.7597 73.7585L18.8759 79.3907L20.7756 81.7126L27.6594 76.0804L25.7597 73.7585Z"
                        fill="#262626"
                      />
                    </svg>
                    <div
                      className={
                        'mx-auto flex w-full max-w-xs items-center gap-x-4 rounded-md border border-neutral-100 bg-white px-8 py-3 text-sm font-semibold text-neutral-800 sm:max-w-sm'
                      }
                    >
                      <span className={'rounded-md bg-blue-50 p-2'}>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          fill={'none'}
                        >
                          <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                          <polyline points="3 7 12 13 21 7"></polyline>
                        </svg>
                      </span>

                      <div>
                        <span className={'block'}>Starting feedback flow in 2 days</span>
                      </div>
                    </div>
                  </motion.div>
                ) : tool === 1 ? (
                  <motion.div
                    key={'transactional'}
                    initial={{opacity: 0, y: -20}}
                    animate={{opacity: 1, y: 0}}
                    exit={{opacity: 0}}
                    className={'flex h-full flex-col items-center justify-center gap-3'}
                  >
                    <div
                      className={
                        'mx-auto flex w-full max-w-xs items-center gap-x-4 rounded-md border border-neutral-100 bg-white px-8 py-3 text-sm text-neutral-800 sm:max-w-sm'
                      }
                    >
                      <span>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          fill={'none'}
                        >
                          <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                          <polyline points="3 7 12 13 21 7"></polyline>
                        </svg>
                      </span>

                      <div>
                        <span className={'block font-semibold'}>Reset your password</span>
                        <span className={'block text-sm text-neutral-500'}>Less than a minute ago</span>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key={'campaigns'}
                    initial={{opacity: 0}}
                    animate={{opacity: 1, rotateY: 0}}
                    exit={{opacity: 0, rotateY: 90}}
                    className={'flex h-full flex-col items-center justify-center gap-3'}
                  >
                    <div
                      className={
                        'mx-auto flex w-full max-w-xs items-center gap-x-4 rounded-md border border-neutral-100 bg-white px-8 py-3 text-sm text-neutral-800 sm:max-w-sm'
                      }
                    >
                      <span>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          fill={'none'}
                        >
                          <path d="M18 21a8 8 0 0 0-16 0" />
                          <circle cx="10" cy="8" r="5" />
                          <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
                        </svg>
                      </span>

                      <div>
                        <span className={'block font-semibold'}>{dayjs().format('MMM')} Announcements</span>
                        <span className={'block text-sm text-neutral-500'}>
                          Sending to {stats?.contacts.toLocaleString()} Contacts
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </section>

        <section className={'space-y-16'}>
          <div className={'w-full space-y-3 text-center'}>
            <h2 className={'text-4xl font-bold text-neutral-800'}>Pricing that doesn't suck</h2>
            <p className={'mx-auto max-w-xl text-balance'}>
              Stop paying for contacts you don't have, start paying for what you use.
            </p>
          </div>

          <div className={'mx-auto flex max-w-lg flex-col items-center'}>
            <div className={'flex items-center justify-center gap-x-4'}>
              <span className={'text-6xl font-bold text-neutral-800'}>$0.001</span>
              <span className={'text-2xl text-neutral-500'}>/email</span>
            </div>

            <div className={'my-24 grid gap-12 sm:grid-cols-3'}>
              <div className={'flex flex-col items-center gap-y-3'}>
                <svg role="img" className={'h-8 w-8'} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <title>MailChimp</title>
                  <path d="M11.267 0C6.791-.015-1.82 10.246 1.397 12.964l.79.669a3.88 3.88 0 0 0-.22 1.792c.084.84.518 1.644 1.22 2.266.666.59 1.542.964 2.392.964 1.406 3.24 4.62 5.228 8.386 5.34 4.04.12 7.433-1.776 8.854-5.182.093-.24.488-1.316.488-2.267 0-.956-.54-1.352-.885-1.352-.01-.037-.078-.286-.172-.586-.093-.3-.19-.51-.19-.51.375-.563.382-1.065.332-1.35-.053-.353-.2-.653-.496-.964-.296-.311-.902-.63-1.753-.868l-.446-.124c-.002-.019-.024-1.053-.043-1.497-.014-.32-.042-.822-.197-1.315-.186-.668-.508-1.253-.911-1.627 1.112-1.152 1.806-2.422 1.804-3.511-.003-2.095-2.576-2.729-5.746-1.416l-.672.285A678.22 678.22 0 0 0 12.7.504C12.304.159 11.817.002 11.267 0zm.073.873c.166 0 .322.019.465.058.297.084 1.28 1.224 1.28 1.224s-1.826 1.013-3.52 2.426c-2.28 1.757-4.005 4.311-5.037 7.082-.811.158-1.526.618-1.963 1.253-.261-.218-.748-.64-.834-.804-.698-1.326.761-3.902 1.781-5.357C5.834 3.44 9.37.867 11.34.873zm3.286 3.273c.04-.002.06.05.028.074-.143.11-.299.26-.413.414a.04.04 0 0 0 .031.064c.659.004 1.587.235 2.192.574.041.023.012.103-.034.092-.915-.21-2.414-.369-3.97.01-1.39.34-2.45.863-3.224 1.426-.04.028-.086-.023-.055-.06.896-1.035 1.999-1.935 2.987-2.44.034-.018.07.019.052.052-.079.143-.23.447-.278.678-.007.035.032.063.062.042.615-.42 1.684-.868 2.622-.926zm3.023 3.205l.056.001a.896.896 0 0 1 .456.146c.534.355.61 1.216.638 1.845.015.36.059 1.229.074 1.478.034.571.184.651.487.751.17.057.33.098.563.164.706.198 1.125.4 1.39.658.157.162.23.333.253.497.083.608-.472 1.36-1.942 2.041-1.607.746-3.557.935-4.904.785l-.471-.053c-1.078-.145-1.693 1.247-1.046 2.201.417.615 1.552 1.015 2.688 1.015 2.604 0 4.605-1.111 5.35-2.072a.987.987 0 0 0 .06-.085c.036-.055.006-.085-.04-.054-.608.416-3.31 2.069-6.2 1.571 0 0-.351-.057-.672-.182-.255-.1-.788-.344-.853-.891 2.333.72 3.801.039 3.801.039a.072.072 0 0 0 .042-.072.067.067 0 0 0-.074-.06s-1.911.283-3.718-.378c.197-.64.72-.408 1.51-.345a11.045 11.045 0 0 0 3.647-.394c.818-.234 1.892-.697 2.727-1.356.281.618.38 1.299.38 1.299s.219-.04.4.073c.173.106.299.326.213.895-.176 1.063-.628 1.926-1.387 2.72a5.714 5.714 0 0 1-1.666 1.244c-.34.18-.704.334-1.087.46-2.863.935-5.794-.093-6.739-2.3a3.545 3.545 0 0 1-.189-.522c-.403-1.455-.06-3.2 1.008-4.299.065-.07.132-.153.132-.256 0-.087-.055-.179-.102-.243-.374-.543-1.669-1.466-1.409-3.254.187-1.284 1.31-2.189 2.357-2.135.089.004.177.01.266.015.453.027.85.085 1.223.1.625.028 1.187-.063 1.853-.618.225-.187.405-.35.71-.401.028-.005.092-.028.215-.028zm.022 2.18a.42.42 0 0 0-.06.005c-.335.054-.347.468-.228 1.04.068.32.187.595.32.765.175-.02.343-.022.498 0 .089-.205.104-.557.024-.942-.112-.535-.261-.872-.554-.868zm-3.66 1.546a1.724 1.724 0 0 0-1.016.326c-.16.117-.311.28-.29.378.008.032.031.056.088.063.131.015.592-.217 1.122-.25.374-.023.684.094.923.2.239.104.386.173.443.113.037-.038.026-.11-.031-.204-.118-.192-.36-.387-.618-.497a1.601 1.601 0 0 0-.621-.129zm4.082.81c-.171-.003-.313.186-.317.42-.004.236.131.43.303.432.172.003.314-.185.318-.42.004-.236-.132-.429-.304-.432zm-3.58.172c-.05 0-.102.002-.155.008-.311.05-.483.152-.593.247-.094.082-.152.173-.152.237a.075.075 0 0 0 .075.076c.07 0 .228-.063.228-.063a1.98 1.98 0 0 1 1.001-.104c.157.018.23.027.265-.026.01-.016.022-.049-.01-.1-.063-.103-.311-.269-.66-.275zm2.26.4c-.127 0-.235.051-.283.148-.075.154.035.363.246.466.21.104.443.063.52-.09.075-.155-.035-.364-.246-.467a.542.542 0 0 0-.237-.058zm-11.635.024c.048 0 .098 0 .149.003.73.04 1.806.6 2.052 2.19.217 1.41-.128 2.843-1.449 3.069-.123.02-.248.029-.374.026-1.22-.033-2.539-1.132-2.67-2.435-.145-1.44.591-2.548 1.894-2.811.117-.024.252-.04.398-.042zm-.07.927a1.144 1.144 0 0 0-.847.364c-.38.418-.439.988-.366 1.19.027.073.07.094.1.098.064.008.16-.039.22-.2a1.2 1.2 0 0 0 .017-.052 1.58 1.58 0 0 1 .157-.37.689.689 0 0 1 .955-.199c.266.174.369.5.255.81-.058.161-.154.469-.133.721.043.511.357.717.64.738.274.01.466-.143.515-.256.029-.067.005-.107-.011-.125-.043-.053-.113-.037-.18-.021a.638.638 0 0 1-.16.022.347.347 0 0 1-.294-.148c-.078-.12-.073-.3.013-.504.011-.028.025-.058.04-.092.138-.308.368-.825.11-1.317-.195-.37-.513-.602-.894-.65a1.135 1.135 0 0 0-.138-.01z" />
                </svg>
                <span className={'text-sm font-medium'}>Mailchimp</span>

                <span className={'text-sm text-neutral-500'}>$0,004 / email</span>
                <span className={'text-sm text-neutral-500'}>Standard plan</span>
              </div>
              <div className={'flex flex-col items-center gap-y-3'}>
                <svg role="img" viewBox="0 0 24 24" className={'h-8 w-8'} xmlns="http://www.w3.org/2000/svg">
                  <title>Twilio Sendgrid</title>
                  <path d="M12 0C5.381-.008.008 5.352 0 11.971V12c0 6.64 5.359 12 12 12 6.64 0 12-5.36 12-12 0-6.641-5.36-12-12-12zm0 20.801c-4.846.015-8.786-3.904-8.801-8.75V12c-.014-4.846 3.904-8.786 8.75-8.801H12c4.847-.014 8.786 3.904 8.801 8.75V12c.015 4.847-3.904 8.786-8.75 8.801H12zm5.44-11.76c0 1.359-1.12 2.479-2.481 2.479-1.366-.007-2.472-1.113-2.479-2.479 0-1.361 1.12-2.481 2.479-2.481 1.361 0 2.481 1.12 2.481 2.481zm0 5.919c0 1.36-1.12 2.48-2.481 2.48-1.367-.008-2.473-1.114-2.479-2.48 0-1.359 1.12-2.479 2.479-2.479 1.361-.001 2.481 1.12 2.481 2.479zm-5.919 0c0 1.36-1.12 2.48-2.479 2.48-1.368-.007-2.475-1.113-2.481-2.48 0-1.359 1.12-2.479 2.481-2.479 1.358-.001 2.479 1.12 2.479 2.479zm0-5.919c0 1.359-1.12 2.479-2.479 2.479-1.367-.007-2.475-1.112-2.481-2.479 0-1.361 1.12-2.481 2.481-2.481 1.358 0 2.479 1.12 2.479 2.481z" />
                </svg>
                <span className={'text-sm font-medium'}>Twilio Sendgrid</span>

                <span className={'text-sm text-neutral-500'}>$0,002 / email</span>
                <span className={'text-sm text-neutral-500'}>Essentials plan</span>
              </div>

              <div className={'flex flex-col items-center gap-y-3'}>
                <svg role="img" viewBox="0 0 24 24" className={'h-8 w-8'} xmlns="http://www.w3.org/2000/svg">
                  <title>Mailgun</title>
                  <path d="M11.837 0c6.602 0 11.984 5.381 11.984 11.994-.017 2.99-3.264 4.84-5.844 3.331a3.805 3.805 0 0 1-.06-.035l-.055-.033-.022.055c-2.554 4.63-9.162 4.758-11.894.232-2.732-4.527.46-10.313 5.746-10.416a6.868 6.868 0 0 1 7.002 6.866 1.265 1.265 0 0 0 2.52 0c0-5.18-4.197-9.38-9.377-9.387C4.611 2.594.081 10.41 3.683 16.673c3.238 5.632 11.08 6.351 15.289 1.402l1.997 1.686A11.95 11.95 0 0 1 11.837 24C2.6 23.72-2.87 13.543 1.992 5.684A12.006 12.006 0 0 1 11.837 0Zm0 7.745c-3.276-.163-5.5 3.281-4.003 6.2a4.26 4.26 0 0 0 4.014 2.31c3.276-.171 5.137-3.824 3.35-6.575a4.26 4.26 0 0 0-3.36-1.935Zm0 2.53c1.324 0 2.152 1.433 1.49 2.58a1.72 1.72 0 0 1-1.49.86 1.72 1.72 0 1 1 0-3.44Z" />
                </svg>
                <span className={'text-sm font-medium'}>Mailgun</span>

                <span className={'text-sm text-neutral-500'}>$0,003 / email</span>
                <span className={'text-sm text-neutral-500'}>Foundation plan</span>
              </div>

              <p className={'text-balance text-center text-xs text-neutral-400 sm:col-span-3'}>
                Calculated based on the plan that best matches the features of Plunk at 10.000 emails per month.
              </p>
            </div>

            <Link
              href={'/pricing'}
              className={
                'mx-auto inline-block rounded-md border border-neutral-300 bg-white px-10 py-4 text-center font-medium transition hover:bg-neutral-50'
              }
            >
              Learn more about pricing
            </Link>
          </div>
        </section>

        <motion.section className={'space-y-16 py-12'} layout>
          <div className={'w-full space-y-3 text-center'}>
            <h2 className={'text-4xl font-bold text-neutral-800'}>Putting privacy first</h2>
            <p className={'mx-auto max-w-xl text-balance'}>
              We take your privacy seriously. That's why we host, store, and process all of your data in the EU.
            </p>
          </div>

          <div className={'mx-auto flex max-w-2xl flex-col items-center'}>
            <div className={'grid gap-12 sm:grid-cols-3'}>
              <div className={'flex flex-col items-center gap-y-3 text-center'}>
                <PackageOpen className={'h-8 w-8'} strokeWidth={2} />
                <span className={'text-sm font-medium'}>Open-Source</span>

                <span className={'text-balance text-sm text-neutral-500'}>See what we're doing under the hood.</span>
              </div>
              <div className={'flex flex-col items-center gap-y-3 text-center'}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={'h-8 w-8'}
                >
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <path d="M12 17h.01" />
                </svg>
                <span className={'text-sm font-medium'}>Transparency</span>

                <span className={'text-balance text-sm text-neutral-500'}>
                  We're open about what we do and how we do it.
                </span>
              </div>
              <div className={'flex flex-col items-center gap-y-3 text-center'}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={'h-8 w-8'}
                >
                  <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                  <line x1="2" x2="22" y1="2" y2="22" />
                </svg>
                <span className={'text-sm font-medium'}>Privacy</span>

                <span className={'text-balance text-sm text-neutral-500'}>
                  Your data is yours. We don't give it to anyone.
                </span>
              </div>
            </div>
          </div>
        </motion.section>

        <section className={'mx-auto flex max-w-6xl flex-col items-center justify-center gap-12 lg:flex-row'}>
          <div className={'space-y-3 text-center lg:text-left'}>
            <h2 className={'text-4xl font-bold text-neutral-800'}>
              Responsive emails,
              <br /> even in Outlook
            </h2>
            <p className={'max-w-sm text-balance'}>
              Use our editor to build responsive emails or bring your own HTML and we'll make it work.
            </p>

            <motion.div layout>
              <Link
                href={'/markdown-to-email'}
                className={
                  'mt-3 inline-block rounded-md border border-neutral-300 bg-white px-10 py-4 text-center font-medium transition hover:bg-neutral-50'
                }
              >
                Try it out for free
              </Link>
            </motion.div>
          </div>

          <div className={'w-full max-w-xl'}>
            <Editor value={template} onChange={val => setTemplate(val)} />
          </div>
        </section>

        <motion.section layout className={'space-y-16'}>
          <div className={'w-full space-y-3 text-center'}>
            <h2 className={'text-4xl font-bold text-neutral-800'}>People love us</h2>
            <p className={'mx-auto max-w-xl'}>Over 1000 companies already use Plunk and we can't blame them.</p>
          </div>

          <div className={'flex flex-col items-center justify-center gap-16'}>
            <div className={'text-center'}>
              <AnimatePresence mode={'wait'}>
                <motion.span
                  key={testimonials[testimonial].author}
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  exit={{opacity: 0}}
                  transition={{
                    type: 'spring',
                    damping: 20,
                    stiffness: 100,
                  }}
                  className={'flex h-40 max-w-2xl flex-col items-center justify-center gap-3 sm:h-32'}
                >
                  <div className={'flex flex-col items-center justify-center gap-9'}>
                    <blockquote className={'mx-auto max-w-xl text-balance text-lg font-medium text-neutral-800'}>
                      {testimonials[testimonial].testimonial}
                    </blockquote>
                    <div className={'flex flex-col items-center gap-y-3'}>
                      <div className={'relative h-10 w-10 overflow-hidden rounded-full'}>
                        {testimonials[testimonial].image}
                      </div>
                      <div className={'space-y-2 text-center text-sm font-medium text-neutral-500'}>
                        <p className={'font-semibold text-neutral-800'}>{testimonials[testimonial].author}</p>
                        <p>{testimonials[testimonial].role}</p>
                      </div>
                    </div>
                  </div>
                </motion.span>
              </AnimatePresence>
            </div>

            <div className={'flex gap-6'}>
              {testimonials.map((t, index) => {
                return (
                  <button
                    name={`Testimonial ${index + 1}`}
                    key={t.author}
                    onClick={() => setTestimonial(index)}
                    className={`${
                      testimonial === index ? 'bg-neutral-400' : 'bg-neutral-200'
                    } h-2 w-2 rounded-full transition`}
                  ></button>
                );
              })}
            </div>
          </div>
        </motion.section>

        <section>
          <div className={'flex flex-col gap-y-12 sm:flex-row sm:items-center'}>
            <div>
              <h2 className={'text-5xl font-extrabold text-neutral-800'}>
                Let's make <br />
                your emails delightful
              </h2>
              <p className={'mt-3 text-balance font-medium text-neutral-600'}>
                Send up to 3000 emails for free every month. <br />
                No credit card required.
              </p>
              <motion.a
                whileHover={{scale: 1.1}}
                whileTap={{scale: 0.9}}
                href={`${DASHBOARD_URI}/auth/signup`}
                className={'mt-9 inline-block rounded-lg bg-neutral-800 px-12 py-4 font-semibold text-white'}
              >
                Start sending for free
              </motion.a>
            </div>
            <div className={'sm:ml-auto'}>
              <div className={'grid gap-x-14 gap-y-10 sm:grid-cols-2 sm:grid-rows-2'}>
                <div className={'flex items-center gap-6'}>
                  <div className={'rounded-md bg-blue-50 p-4'}>
                    <svg
                      className={'h-7 w-7'}
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      fill={'none'}
                    >
                      <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                      <polyline points="3 7 12 13 21 7"></polyline>
                    </svg>
                  </div>
                  <div>
                    <p className={'text-3xl font-extrabold text-neutral-800'}>
                      <AnimatedNumber value={stats?.emails ?? 0} />
                    </p>
                    <p className={'font-medium text-neutral-600'}>Emails delivered</p>
                  </div>
                </div>
                <div className={'flex items-center gap-6'}>
                  <div className={'rounded-md bg-blue-50 p-4'}>
                    <svg
                      className={'h-7 w-7'}
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      fill={'none'}
                    >
                      <path d="M8 9l3 3l-3 3"></path>
                      <line x1="13" y1="15" x2="16" y2="15"></line>
                      <rect x="3" y="4" width="18" height="16" rx="2"></rect>
                    </svg>
                  </div>
                  <div>
                    <p className={'text-3xl font-extrabold text-neutral-800'}>
                      <AnimatedNumber value={stats?.events ?? 0} />
                    </p>
                    <p className={'font-medium text-neutral-600'}>Events processed</p>
                  </div>
                </div>
                <div className={'flex items-center gap-6'}>
                  <div className={'rounded-md bg-blue-50 p-4'}>
                    <svg
                      className={'h-7 w-7'}
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      fill={'none'}
                    >
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path>
                    </svg>
                  </div>
                  <div>
                    <p className={'text-3xl font-extrabold text-neutral-800'}>
                      <AnimatedNumber value={stats?.projects ?? 0} />
                    </p>
                    <p className={'font-medium text-neutral-600'}>Projects hosted</p>
                  </div>
                </div>
                <div className={'flex items-center gap-6'}>
                  <div className={'rounded-md bg-blue-50 p-4'}>
                    <svg
                      className={'h-7 w-7'}
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      fill={'none'}
                    >
                      <circle cx="12" cy="7" r="4"></circle>
                      <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                    </svg>
                  </div>
                  <div>
                    <p className={'text-3xl font-extrabold text-neutral-800'}>
                      <AnimatedNumber value={stats?.contacts ?? 0} />
                    </p>
                    <p className={'font-medium text-neutral-600'}>Contacts stored</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

/**
 *
 */
export async function getStaticProps() {
  await generateRssFeed();

  return {
    props: {},
  };
}
